/* F02d - keep the shopper's own option choices in the quick-view picker.

   Why this exists
   ---------------
   VariantSelects.disableUnavailable() (shared, also used by the product page)
   does this when the shown combination has no variant: it unchecks the last
   option group and then walks its values, checking each in turn, until a
   variant resolves. So picking a colour that does not come in the chosen size
   silently moves the size to the first one that happens to be available.

   That is a size chosen for the shopper, not by them. This element restores the
   shopper's own choice, leaves the unavailable combination visible, and blocks
   the add button until the shopper picks an available combination themselves.
   When it does allow an add, it checks that the variant id the form is about to
   submit really is the variant for the options on screen, and corrects it if the
   theme left a stale one behind.

   Scope: it only does anything where <steng-strict-variant> is rendered, which
   is sections/quick-view.liquid alone. The shared class is not modified and the
   product page is untouched.

   Strings: reuses window.variantStrings.unavailable, already translated in both
   locales (products.product.unavailable). No new copy.
*/
(function () {
  'use strict';
  if (!window.customElements || window.customElements.get('steng-strict-variant')) return;

  var ACTIVE = 'swatch-active';

  function attrEscape(v) { return String(v).replace(/"/g, '\\"'); }

  class StengStrictVariant extends HTMLElement {
    connectedCallback() {
      if (this.__stengInit) return;
      // The popup injects the section with innerHTML and may strip the
      // .shopify-section wrapper, so walk up to the nearest ancestor that holds
      // both the picker and the add button rather than assuming a class.
      var root = this.parentElement;
      while (root && !(root.querySelector('variant-radios, variant-selects') &&
                       root.querySelector('button[name="add"]'))) {
        root = root.parentElement;
      }
      if (!root) return;
      this.root = root;
      this.picker = root.querySelector('variant-radios, variant-selects');
      if (!this.picker || !this.button()) return;
      if (this.groups().length < 2) return;   // one group cannot be overridden
      this.__stengInit = true;
      this.blocked = 0;        // counters, for verification
      this.corrected = 0;

      this.wanted = this.groups().map(function (g) {
        var c = g.querySelector('input:checked');
        return c ? c.value : null;
      });

      // 1. capture phase: record what the shopper clicked, before the theme reacts
      this.onCapture = function (e) {
        var gi = this.groups().findIndex(function (g) { return g.contains(e.target); });
        if (gi > -1 && e.target && e.target.value != null) this.wanted[gi] = e.target.value;
      }.bind(this);
      this.picker.addEventListener('change', this.onCapture, true);

      // 2. bubble phase: the theme's own handler has already run synchronously,
      //    so any auto-move has happened and can be undone here.
      this.onChange = function () {
        this.enforce();
        setTimeout(this.enforce.bind(this), 0);   // survive any async re-render
      }.bind(this);
      this.picker.addEventListener('change', this.onChange, false);

      // 3. Gate the submit. The theme re-renders the button region on every
      //    variant change, so the button element is replaced repeatedly - these
      //    listeners are delegated from the document and scoped by root rather
      //    than attached to a button that will not survive. They are removed in
      //    disconnectedCallback so repeated opens do not stack them up.
      this.onGuard = this.guard.bind(this);
      document.addEventListener('click', this.onGuard, true);
      document.addEventListener('submit', this.onGuard, true);

      // renderProductInfo() replaces the button region over the network, which
      // can land after the change handler has finished and would hand back a
      // fresh, enabled button, or reset the checked inputs. Re-run on any change
      // to this subtree. enforce() and paint() are both idempotent, so a quiet
      // DOM writes nothing and the observer settles instead of looping.
      if (window.MutationObserver) {
        this.observer = new MutationObserver(function () { this.enforce(); }.bind(this));
        this.observer.observe(root, { childList: true, subtree: true });
      }

      this.enforce();
    }

    disconnectedCallback() {
      if (!this.__stengInit) return;
      document.removeEventListener('click', this.onGuard, true);
      document.removeEventListener('submit', this.onGuard, true);
      if (this.picker) {
        this.picker.removeEventListener('change', this.onCapture, true);
        this.picker.removeEventListener('change', this.onChange, false);
      }
      if (this.observer) { this.observer.disconnect(); this.observer = null; }
      this.root = null;
      this.picker = null;
      this.__stengInit = false;
    }

    button() {
      return this.root ? this.root.querySelector('button[name="add"]') : null;
    }

    groups() {
      return this.picker ? Array.prototype.slice.call(this.picker.querySelectorAll('fieldset')) : [];
    }

    variants() {
      // key the cache on the script node: a re-render swaps in a new one, and a
      // stale table would be judged against the wrong product
      var s = this.picker && this.picker.querySelector('script[type="application/json"]');
      if (!s) return [];
      if (this.__vSrc !== s) {
        this.__vSrc = s;
        try { this.__variants = JSON.parse(s.textContent); }
        catch (e) { this.__variants = []; }
      }
      return this.__variants || [];
    }

    shown() {
      return this.groups().map(function (g) {
        var c = g.querySelector('input:checked');
        return c ? c.value : null;
      });
    }

    expected() {
      var chosen = this.shown();
      if (!chosen.length || chosen.indexOf(null) > -1) return null;
      return this.variants().find(function (v) {
        return chosen.every(function (val, i) { return v.options[i] === val; });
      }) || null;
    }

    /* The gate. Runs for click and submit, in capture, before the theme's own
       handlers, and only for events inside this quick view. */
    guard(e) {
      if (!this.isConnected || !this.root || !this.root.contains(e.target)) return;
      var btn = e.target.closest && e.target.closest('button[name="add"]');
      if (e.type === 'click' && !btn) return;

      if (this.unresolved) {
        this.blocked++;
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      // Allowed - but prove the form is about to send the variant that is on
      // screen. A late section response can leave a stale id behind, and the
      // shopper must never get something other than what they selected.
      var exp = this.expected();
      if (!exp) {                       // nothing matches what is shown
        this.blocked++;
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      var target = btn || this.button();
      var form = target && (target.form || target.closest('form'));
      var idInput = form && form.querySelector('[name="id"]');
      if (idInput && String(idInput.value) !== String(exp.id)) {
        idInput.value = exp.id;
        this.corrected++;
      }
    }

    enforce() {
      var groups = this.groups();
      if (groups.length < 2) return;

      // put back any group the theme moved away from the shopper's choice
      groups.forEach(function (g, gi) {
        var want = this.wanted[gi];
        if (want == null) return;
        var cur = g.querySelector('input:checked');
        if (cur && cur.value === want) return;
        var el = g.querySelector('input[value="' + attrEscape(want) + '"]');
        if (!el) return;                       // not offered for this product
        if (cur) this.setChecked(cur, false);
        this.setChecked(el, true);
      }.bind(this));

      var match = this.expected();
      this.unresolved = !match || match.available === false;
      this.paint();
    }

    setChecked(input, on) {
      input.checked = on;
      var label = input.closest('label') || input.parentElement;
      if (label) label.classList.toggle(ACTIVE, on);
    }

    paint() {
      // the button is re-rendered by the theme, so re-read it and its labels
      var btn = this.button();
      if (!btn) return;
      // Idempotence is the re-entry guard. MutationObserver callbacks are
      // asynchronous, so a "painting" flag is already cleared by the time the
      // callback runs and would loop forever. Writing nothing when the button
      // already carries the intended state means the observer simply settles.
      var want = !!this.unresolved;
      var has = btn.getAttribute('data-steng-unavailable') !== null;
      if (want === has) return;
      this.paintInto(btn, want);
    }

    paintInto(btn, want) {
      var spans = btn.querySelectorAll('.btn-txt, .btn-hover-txt');
      if (!spans.length) spans = [btn];
      var msg = (window.variantStrings && window.variantStrings.unavailable) || '';
      if (want) {
        btn.setAttribute('data-steng-orig', JSON.stringify(
          Array.prototype.map.call(spans, function (s) { return s.textContent; })));
        btn.setAttribute('data-steng-unavailable', '');
        if (msg) Array.prototype.forEach.call(spans, function (s) { s.textContent = msg; });
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        this.setAttribute('data-steng-unresolved', '');
      } else {
        var orig = [];
        try { orig = JSON.parse(btn.getAttribute('data-steng-orig') || '[]'); } catch (e) {}
        Array.prototype.forEach.call(spans, function (s, i) {
          if (orig[i] != null && orig[i] !== s.textContent) s.textContent = orig[i];
        });
        btn.removeAttribute('data-steng-unavailable');
        btn.removeAttribute('data-steng-orig');
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        this.removeAttribute('data-steng-unresolved');
      }
    }
  }

  window.customElements.define('steng-strict-variant', StengStrictVariant);
})();
